import { InviteUser } from 'components/UsersComponent/invite-user';
import { EntityTableContext } from 'components/Entity/EntityTableContext';
import { localizeDigitsForLocale } from 'i18n/arabicDigits';
import * as _ from 'lodash';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { User } from 'store/auth/models';
import { Entity } from 'store/organisation/models';
import { getUserLoading, getUserMembers } from 'store/user/selectors';
import {
  AvatarGroupRenderer as SharedAvatarGroupRenderer,
  AvatarSizes,
} from '@monorepo/shared-lib';

interface AvatarGroupRendererProps {
  entity: Entity;
  maxCount?: number;
  size: AvatarSizes;
  /** Expand the + Add User chip leftward (fixed right-edge toolbars). */
  expandToggleLeft?: boolean;
}

const AvatarGroupRendererView: React.FC<
  AvatarGroupRendererProps & {
    members?: User[];
    loading: boolean;
  }
> = ({
  entity,
  maxCount,
  size,
  expandToggleLeft = false,
  members,
  loading,
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const openMemberWindow = () => {
    const modalData: DrawerData = {
      type: DrawerType.INVITE_USER,
      title: `${t('common.share')} “${entity.organisation} ”`,
      component: (
        <InviteUser
          loading={loading}
          members={entity.users}
          allUsers={_.differenceBy(members, entity.users, 'accountUuid')}
          organisationId={entity.organisationId}
        />
      ),
    };

    dispatch(turnDrawerOn(modalData));
  };

  return (
    <SharedAvatarGroupRenderer
      users={entity.users ?? []}
      size={size}
      maxCount={maxCount}
      expandToggleLeft={expandToggleLeft}
      onAddUser={openMemberWindow}
      addUserLabel={t('users.add-user')}
      formatOverflow={(count) =>
        `+${localizeDigitsForLocale(count, i18n.language)}`
      }
    />
  );
};

const AvatarGroupRendererConnected: React.FC<AvatarGroupRendererProps> = (
  props
) => {
  const members = useSelector(getUserMembers);
  const loading = useSelector(getUserLoading);
  return (
    <AvatarGroupRendererView
      {...props}
      members={members}
      loading={!!loading}
    />
  );
};

export const AvatarGroupRenderer: React.FC<AvatarGroupRendererProps> = (
  props
) => {
  const table = useContext(EntityTableContext);
  if (table) {
    return (
      <AvatarGroupRendererView
        {...props}
        members={table.members}
        loading={table.membersLoading}
      />
    );
  }
  return <AvatarGroupRendererConnected {...props} />;
};
